import { I18nProvider } from '@e2grnd/ui-core'
import { ChromelessLayout } from '@e2grnd/ui-core/dist/components/ChromelessLayout'
import Layout from '@e2grnd/ui-core/dist/components/Layout'
import HelpProvider from '@e2grnd/ui-core/dist/components/TopNav/HelpProvider'
import AppProvider from '@e2grnd/ui-core/dist/contexts/AppProvider'
import AuthedAppProvider from '@e2grnd/ui-core/dist/contexts/AuthedAppProvider'
import useChromeless from '@e2grnd/ui-core/dist/hooks/useChromeless'
import type React from 'react'
import { type ComponentProps, useMemo } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import type { PageFrontmatterOptions } from 'types/page-frontmatter'
import platformChangeLogs from '../meta/platform-change-logs.json'
// Import metadata directly for Vite
import siteMetadata from '../meta/site-metadata.json'
import AssetTableParamsProviderWrapper from './AssetTableParamsProviderWrapper'
import CalculatorDescriptorsProvider from './CalculatorDescriptorsProvider'

import '@e2grnd/ui-core/dist/styles/css/tachyons.min.css'
import '@e2grnd/ui-core/dist/styles/index.css'

// Locale data loaded asynchronously — only the active locale downloads on
// initial load. Tolgee's TolgeeStaticData accepts () => Promise<data>.
const localeLoaders = {
  'en-US': () => import('../i18n/locales/en-US.json').then(m => m.default),
  'es-US': () => import('../i18n/locales/es-US.json').then(m => m.default),
  'ja-JP': () => import('../i18n/locales/ja-JP.json').then(m => m.default),
  'ar-SA': () => import('../i18n/locales/ar-SA.json').then(m => m.default),
}

interface ViteLayoutProps {
  children: React.ReactNode
  pageContext?: {
    frontmatter?: PageFrontmatterOptions
  }
  location?: {
    pathname: string
  }
}

export default function CalculatorLayout(props: ViteLayoutProps) {
  const { children, pageContext, location } = props

  const pkg = siteMetadata.package
  const layoutProps: ComponentProps<typeof Layout> = pageContext?.frontmatter?.layout || {}

  layoutProps.footerProps = {
    version: pkg?.version,
    uiCoreVersion: pkg?.uiCoreVersion,
    buildTime: siteMetadata.buildTime,
  }

  const changeLogData = useMemo(() => platformChangeLogs?.concat(), [])

  layoutProps.topNavProps = {
    platformChangeLogs: changeLogData,
  }

  const localeStatics = localeLoaders
  const isChromeless = useChromeless()
  const LayoutComponent = isChromeless ? ChromelessLayout : Layout

  // Bare layout - minimal wrapper
  if (pageContext?.frontmatter?.bare) {
    return (
      <HelmetProvider>
        <I18nProvider staticData={localeStatics}>
          <CalculatorDescriptorsProvider>
            <HelpProvider>{children}</HelpProvider>
          </CalculatorDescriptorsProvider>
        </I18nProvider>
      </HelmetProvider>
    )
  }

  // Authenticated app routes (under /app)
  if (location?.pathname?.startsWith('/app')) {
    return (
      <I18nProvider staticData={localeStatics}>
        <AssetTableParamsProviderWrapper>
          <CalculatorDescriptorsProvider>
            <AppProvider platformName={pkg?.name || undefined} platformVersion={pkg?.version || undefined}>
              <AuthedAppProvider>
                <HelpProvider>
                  <LayoutComponent {...layoutProps}>{children}</LayoutComponent>
                </HelpProvider>
              </AuthedAppProvider>
            </AppProvider>
          </CalculatorDescriptorsProvider>
        </AssetTableParamsProviderWrapper>
      </I18nProvider>
    )
  }

  // Public routes (default)
  return (
    <I18nProvider staticData={localeStatics}>
      <AssetTableParamsProviderWrapper>
        <CalculatorDescriptorsProvider>
          <AppProvider>
            <HelpProvider>
              <LayoutComponent {...layoutProps}>{children}</LayoutComponent>
            </HelpProvider>
          </AppProvider>
        </CalculatorDescriptorsProvider>
      </AssetTableParamsProviderWrapper>
    </I18nProvider>
  )
}
